import * as React from 'react';
import { DataTable, DataRow, DataCell, DataTableButton } from 'components/basic/DataTable';
import { saveAs } from 'file-saver';
import { ConversionCleanupReportModel } from '../../services/conversionCleanupReportService';

interface CleanupReportGridProps {
    records: ConversionCleanupReportModel[];
    retentionDays: number;
    searchText: string;
    conversionTypes: string[];
    conversionTypeFilter: string;
    onSearchChange: (value: string) => void;
    onConversionTypeChange: (value: string) => void;
    onClearFilters: () => void;
    onRowClick?: (id: string) => void;
}

interface CleanupReportGridState {
    sortColumn: keyof ConversionCleanupReportModel | null;
    sortDescending: boolean;
}

export class CleanupReportGrid extends React.Component<CleanupReportGridProps, CleanupReportGridState> {
    constructor(props: CleanupReportGridProps) {
        super(props);
        this.state = {
            sortColumn: null,
            sortDescending: false,
        };
    }

    private handleSort = (column: keyof ConversionCleanupReportModel) => {
        this.setState(prev => ({
            sortColumn: column,
            sortDescending: prev.sortColumn === column ? !prev.sortDescending : false,
        }));
    };

    private getRowPriority(record: ConversionCleanupReportModel): number {
        if (record.isEligibleForDeletion) return 1;
        if (record.retentionCutoffDate) {
            const cutoff = new Date(record.retentionCutoffDate);
            const tenDaysFromNow = new Date();
            tenDaysFromNow.setDate(tenDaysFromNow.getDate() + 10);
            if (cutoff <= tenDaysFromNow) return 2;
        }
        if (record.doNotArchive) return 4;
        return 3;
    }

    private getSortedRecords(): ConversionCleanupReportModel[] {
        const { records } = this.props;
        const { sortColumn, sortDescending } = this.state;

        if (sortColumn === null) {
            return [...records].sort((a, b) => {
                const aDate = a.retentionCutoffDate ? new Date(a.retentionCutoffDate).getTime() : 0;
                const bDate = b.retentionCutoffDate ? new Date(b.retentionCutoffDate).getTime() : 0;
                return aDate - bDate;
            });
        }

        return [...records].sort((a, b) => {
            const aVal = a[sortColumn] ?? '';
            const bVal = b[sortColumn] ?? '';

            if (aVal < bVal) return sortDescending ? 1 : -1;
            if (aVal > bVal) return sortDescending ? -1 : 1;
            return 0;
        });
    }

    private formatDate(dateStr: string | null): string {
        if (!dateStr) return '—';
        try {
            return new Date(dateStr).toLocaleDateString();
        } catch {
            return dateStr;
        }
    }

    private renderBoolIcon(value: boolean) {
        return value
            ? <p className="mapped">&#x2705;</p>
            : <p className="mapped">&#x274C;</p>;
    }

    private exportToCsv = () => {
        const sortedRecords = this.getSortedRecords();
        const headers = [
            '#', 'Conversion Name', 'Conversion Type', 'Run Type', 'Created By',
            'Go Live Date', 'Retention Date',
            'Eligible for Archive', 'Do Not Archive', 'Reason',
        ];
        const escape = (value: string | number | boolean | null | undefined): string => {
            const str = value == null ? '' : String(value);
            return `"${str.replace(/"/g, '""')}"`;
        };
        const formatCsvDate = (d: string | null) => escape(this.formatDate(d));
        const rows = sortedRecords.map((record, index) => [
            index + 1,
            escape(record.conversionName),
            escape(record.conversionType),
            escape(record.runType),
            escape(record.createdByName || record.createdByEmail),
            formatCsvDate(record.goLiveDate),
            formatCsvDate(record.retentionCutoffDate),
            record.isEligibleForDeletion ? 'Yes' : 'No',
            record.doNotArchive ? 'Yes' : 'No',
            escape(record.deletionException),
        ].join(','));
        const csv = [headers.join(','), ...rows].join('\r\n');
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, `ArchivedStatusofConversionProjects_${new Date().toISOString().slice(0, 10)}.csv`);
    };

    private getCreatedByDisplayName(record: ConversionCleanupReportModel): string {
        return record.createdByName || record.createdByEmail;
    }

    private getRowClass(record: ConversionCleanupReportModel): string {
        if (record.doNotArchive) {
            return 'cleanup-report-do-not-archive';
        }
        if (record.isEligibleForDeletion) {
            return 'cleanup-report-eligible';
        }
        if (record.retentionCutoffDate) {
            const cutoff = new Date(record.retentionCutoffDate);
            const tenDaysFromNow = new Date();
            tenDaysFromNow.setDate(tenDaysFromNow.getDate() + 10);
            if (cutoff <= tenDaysFromNow) {
                return 'cleanup-report-nearing';
            }
        }
        return '';
    }

    public render() {
        const sortedRecords = this.getSortedRecords();
        const { onRowClick, retentionDays, searchText, conversionTypeFilter, conversionTypes, onSearchChange, onConversionTypeChange, onClearFilters } = this.props;

        return (
            <>
                <DataTable fixed>
                <thead>
                    <tr className='nonFullWidthRow' style={{ minWidth: 'unset' }}>
                        <th colSpan={10} style={{ padding: '6px 8px', borderBottom: '1px solid #dee2e6', overflow: 'hidden' }}>
                            <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 'clamp(6px, 0.8vw, 14px)', fontSize: 'clamp(9px, 0.75vw, 12px)', width: '100%', overflow: 'hidden' }}>
                                <span style={{ fontWeight: 'bold', fontSize: 'calc(clamp(9px, 0.75vw, 12px) * 1.4)', whiteSpace: 'nowrap', flexShrink: 0 }}>Archived Status of Conversion Projects</span>
                                <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 'clamp(4px, 0.5vw, 8px)', flexShrink: 0 }}>
                                    <select
                                        className="custom-select"
                                        style={{ width: 'clamp(100px, 11vw, 240px)', fontSize: 'inherit', flexShrink: 0 }}
                                        value={conversionTypeFilter}
                                        onChange={e => onConversionTypeChange(e.target.value)}
                                    >
                                        <option value="">Conversion Types</option>
                                        {conversionTypes.map(conversionType => (
                                            <option key={conversionType} value={conversionType}>{conversionType}</option>
                                        ))}
                                    </select>
                                    <input
                                        type="text"
                                        className="form-control"
                                        style={{ width: 'clamp(80px, 11vw, 220px)', fontSize: 'inherit', flexShrink: 0 }}
                                        placeholder="Search"
                                        value={searchText}
                                        onChange={e => onSearchChange(e.target.value)}
                                    />
                                    <button type="button" className="btn btn-secondary material-icon" aria-label="Clear search" style={{ fontSize: 'inherit', flexShrink: 0 }} onClick={onClearFilters}>
                                        close
                                    </button>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 'clamp(2px, 0.5vw, 8px)', marginLeft: 'auto', flexShrink: 1, minWidth: 0, overflow: 'hidden', fontSize: 'inherit' }}>
                                    {retentionDays > 0 && (
                                        <>
                                            <span style={{ whiteSpace: 'nowrap', fontWeight: 'bold', fontSize: 'inherit', flexShrink: 0 }}>
                                                Retention Period: {retentionDays} days
                                            </span>
                                            <span style={{ color: '#ccc', fontWeight: 'normal', flexShrink: 0 }}>|</span>
                                        </>
                                    )}
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>
                                        <span style={{ display: 'inline-block', width: 'clamp(10px, 0.8vw, 14px)', height: 'clamp(10px, 0.8vw, 14px)', backgroundColor: '#ffffff', border: '1px solid #ccc', flexShrink: 0 }}></span>
                                        <span style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', lineHeight: 1.1, minWidth: 0, overflow: 'hidden' }}>
                                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Within</span>
                                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>retention period</span>
                                        </span>
                                    </span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>
                                        <span style={{ display: 'inline-block', width: 'clamp(10px, 0.8vw, 14px)', height: 'clamp(10px, 0.8vw, 14px)', backgroundColor: '#fff3cd', border: '1px solid #ccc', flexShrink: 0 }}></span>
                                        <span style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', lineHeight: 1.1, minWidth: 0, overflow: 'hidden' }}>
                                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Within</span>
                                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>10 days to cross retention period</span>
                                        </span>
                                    </span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>
                                        <span style={{ display: 'inline-block', width: 'clamp(10px, 0.8vw, 14px)', height: 'clamp(10px, 0.8vw, 14px)', backgroundColor: '#ffcccc', border: '1px solid #ccc', flexShrink: 0 }}></span>
                                        <span style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', lineHeight: 1.1, minWidth: 0, overflow: 'hidden' }}>
                                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Past</span>
                                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>retention period</span>
                                        </span>
                                    </span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>
                                        <span style={{ display: 'inline-block', width: 'clamp(10px, 0.8vw, 14px)', height: 'clamp(10px, 0.8vw, 14px)', backgroundColor: '#cce5ff', border: '1px solid #ccc', flexShrink: 0 }}></span>
                                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flexShrink: 1 }}>Do Not Archive</span>
                                    </span>
                                    <span style={{ flexShrink: 0 }}>
                                        <DataTableButton
                                            icon='file_download'
                                            id='cleanupReportExport'
                                            title='Export to Excel'
                                            action={this.exportToCsv}
                                        />
                                    </span>
                                </div>
                            </div>
                        </th>
                    </tr>
                    <tr className='nonFullWidthRow' style={{ minWidth: 'unset' }}>
                        <th className="number-column" style={{ width: '3%', verticalAlign: 'middle', textAlign: 'center' }}>#</th>
                        <th onClick={() => this.handleSort('conversionName')} style={{ width: '12%', verticalAlign: 'middle', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis' }}>Conversion Name<i className="fa fa-sort"></i></th>
                        <th onClick={() => this.handleSort('conversionType')} style={{ width: '10%', verticalAlign: 'middle', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis' }}>Conversion Type<i className="fa fa-sort"></i></th>
                        <th onClick={() => this.handleSort('runType')} style={{ width: '6%', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Run Type<i className="fa fa-sort"></i></th>
                        <th onClick={() => this.handleSort('createdByName')} style={{ width: '12%', verticalAlign: 'middle', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis' }}>Created By<i className="fa fa-sort"></i></th>
                        <th onClick={() => this.handleSort('goLiveDate')} style={{ width: '8%', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Go Live Date<i className="fa fa-sort"></i></th>
                        <th onClick={() => this.handleSort('retentionCutoffDate')} style={{ width: '9%', verticalAlign: 'middle', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis' }}>Retention Date<i className="fa fa-sort"></i></th>
                        <th onClick={() => this.handleSort('isEligibleForDeletion')} style={{ width: '8%', verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Eligible for Archive<i className="fa fa-sort"></i></th>
                        <th onClick={() => this.handleSort('doNotArchive')} style={{ width: '11%', verticalAlign: 'middle', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis' }}>Do Not Archive<i className="fa fa-sort"></i></th>
                        <th style={{ width: '10%', verticalAlign: 'middle', textAlign: 'center' }}>Reason</th>
                    </tr>
                </thead>
                <tbody className="nonEntity">
                    {sortedRecords.map((record, index) => {
                        return (
                        <DataRow key={record.id} id={record.id} className={this.getRowClass(record)}
                            onClick={() => onRowClick && onRowClick(record.id)}
                            style={{ cursor: onRowClick ? 'pointer' : undefined }}>
                            <DataCell style={{ width: '3%' }}>{index + 1}</DataCell>
                            <DataCell style={{ width: '12%' }} title={record.conversionName}>{record.conversionName}</DataCell>
                            <DataCell style={{ width: '10%' }} title={record.conversionType}>{record.conversionType}</DataCell>
                            <DataCell style={{ width: '6%', whiteSpace: 'nowrap' }} title={record.runType}>{record.runType}</DataCell>
                            <DataCell style={{ width: '12%' }} title={this.getCreatedByDisplayName(record)}>{this.getCreatedByDisplayName(record)}</DataCell>
                            <DataCell style={{ width: '8%', whiteSpace: 'nowrap' }} title={this.formatDate(record.goLiveDate)}>{this.formatDate(record.goLiveDate)}</DataCell>
                            <DataCell style={{ width: '9%' }} title={this.formatDate(record.retentionCutoffDate)}>{this.formatDate(record.retentionCutoffDate)}</DataCell>
                            <DataCell style={{ width: '8%' }}>{this.renderBoolIcon(record.isEligibleForDeletion)}</DataCell>
                            <DataCell style={{ width: '11%' }}>{this.renderBoolIcon(record.doNotArchive)}</DataCell>
                            <DataCell style={{ width: '10%' }}>
                                <span style={{ textAlign: 'left', display: 'block' }}>{record.deletionException ?? ''}</span>
                            </DataCell>
                        </DataRow>
                        );
                    })}
                    {sortedRecords.length === 0 && (
                        <tr>
                            <td colSpan={10} className="text-center">No conversion projects found.</td>
                        </tr>
                    )}
                </tbody>
            </DataTable>
            </>
        );
    }
}

